import type { IAsset } from '@frontend/components/interfaces/asset';
import type { ICustomControlProcedureReadProps } from '@frontend/components/pages/customControls/interfaces/';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ProcedureStatus } from '@common/enums/procedureStatus';
import { SomnologyOrganizationType } from '@common/enums/somnologyOrganizationType';
import { getDisplayName } from '@common/utils/getDisplayName';
import { watchpatSleepRecorderTypes } from '@common/validation/customControls/somnology';
import { ProcedureReadButtons } from '@frontend/components/elements/carePathButtons/ProcedureReadButtons';
import { Button, ButtonContainer } from '@frontend/components/elements/common';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroup } from '@frontend/components/elements/forms/HorizontalFormGroup';
import { ProcedureAssetThumbnails, ProcessContainer } from '@frontend/components/pages/episodes/common';
import { ProcessItemHeader } from '@frontend/components/pages/episodes/ProcessItemHeader';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { useEpisodeContext } from '@frontend/utils/hooks/useEpisodeContext';
import { OsasoV1QuestionnairesRead } from './OsasoV1QuestionnairesRead';

interface ICustomControlData {
    nightlyReportMedical: {
        [key: string]: {
            assetId: string;
        };
    };
    nightlyReports: string[];
    summaryReportMedical: {
        assetId: string;
    };
}

export function OsasoV1ProcedureRead(props: Readonly<ICustomControlProcedureReadProps>) {
    const { patient } = useEpisodeContext();
    const language = getSelectedLanguage();

    if (
        !props.request.customControlData ||
        props.request.customControlData.somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter
    ) {
        return null;
    }

    const assets = !watchpatSleepRecorderTypes.includes(props.request.customControlData.typeSlaaprecorder)
        ? getKsyosMedicalCenterAssets(props.procedure.customControlData)
        : props.procedure.assets;

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure?.dateSent}
                isFirstItem
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails &&
                    props.process.procedure &&
                    props.process.procedure.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <HorizontalFormGroup>
                <FormLabel>{language.request.attachment}</FormLabel>
                <ProcedureAssetThumbnails
                    assets={assets}
                    procedureId={props.procedure.id}
                />
            </HorizontalFormGroup>

            <OsasoV1QuestionnairesRead {...props} />

            {props.request.customControlData.manuallyRequested &&
                props.procedure.status === ProcedureStatus.completed && <ProcedureReadButtons {...props} />}

            {props.procedure.status !== ProcedureStatus.completed &&
                props.procedure.customControlData.isLastWatchPatSleepStudyIncomplete === true && (
                    <ButtonContainer
                        alignRight
                        buttons={[
                            <Button
                                automation="createOsasc2"
                                color="secondary"
                                key="createOsasc2"
                                large
                                onClick={async () => {
                                    await fetchJson(
                                        `/api/requests/${props.request.id}/closeIncompleteWatchPatOsaso`,
                                        'POST',
                                        {}
                                    );
                                    window.location.reload();
                                }}
                            >
                                {getSelectedLanguage().osas.startOsasc2}
                            </Button>,
                        ]}
                    />
                )}
        </ProcessContainer>
    );
}

export function getKsyosMedicalCenterAssets(customControlData: ICustomControlData) {
    const assets: IAsset[] = [];

    if (customControlData) {
        if (customControlData.summaryReportMedical) {
            assets.push({ filename: 'samenvatting.pdf', id: customControlData.summaryReportMedical.assetId });
        }

        customControlData.nightlyReports?.forEach((nightlyReportId, index) => {
            assets.push({
                filename: 'rapport_' + (index + 1) + '.pdf',
                id: customControlData.nightlyReportMedical[nightlyReportId].assetId,
            });
        });
    }

    return assets;
}
